/**
 * startFlow() — step-funnel helper for multi-step workflows (PRD §5).
 *
 * Owns: a shared flow id, auto-incrementing step_index, and abandonment
 * semantics (explicit in-app exit only; idempotent; suppressed after
 * complete()). Unload-time delivery is best-effort via the SDK's native
 * keepalive:true POSTs (tech-spec T1 resolved) — no custom sendBeacon.
 */
import { devWarn } from "./devWarn";
import { track } from "./track";
import type { EventMap, EventName } from "./registry";

export type FlowName =
  | "booking_doctor"
  | "booking_diagnostic"
  | "disposition"
  | "lead_create"
  | "patient_registration"
  | "export"
  | "bulk_update";

/** The *_abandoned event fired per flow. */
export const FLOW_ABANDON_EVENT: Record<FlowName, EventName> = {
  booking_doctor: "booking_abandoned",
  booking_diagnostic: "booking_abandoned",
  disposition: "disposition_abandoned",
  lead_create: "lead_create_abandoned",
  patient_registration: "patient_registration_abandoned",
  export: "export_abandoned",
  bulk_update: "bulk_update_abandoned",
};

export interface FlowHandle {
  /** Correlation id shared by every event in this flow run (e.g. booking_id). */
  readonly flowId: string;
  /** Emit a step event; step_index is stamped automatically (1-based, ordered). */
  step<E extends EventName>(
    event: E,
    props: Omit<EventMap[E], "step_index">
  ): void;
  /** Emit the terminal event and suppress any later abandon(). */
  complete<E extends EventName>(
    event: E,
    props: Omit<EventMap[E], "step_index">
  ): void;
  /** Fire the flow's *_abandoned event (idempotent; no-op after complete()). */
  abandon(): void;
}

/** track() with a dynamic-props signature — the single boundary cast this orchestrator needs. */
const emitTrack = track as unknown as (
  event: EventName,
  props: Record<string, unknown>
) => void;

function newFlowId(): string {
  const uuid = globalThis.crypto?.randomUUID?.();
  return (
    uuid ??
    `fl_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`
  );
}

/**
 * Start a step-funnel run.
 * @param flow  which funnel — determines the *_abandoned event.
 * @param seed  props merged into every event of the run (e.g. {opportunity_id}
 *              for disposition, {booking_type} for booking wizards).
 */
export function startFlow(
  flow: FlowName,
  seed?: Record<string, string | number | boolean>
): FlowHandle {
  const flowId = newFlowId();
  const startedAt = Date.now();
  let stepIndex = 0;
  let lastStep = "";
  let terminated = false;

  function emit(event: EventName, extra: Record<string, unknown>): void {
    try {
      // Stamp the stable correlation id as `flow_id` (a reserved system prop),
      // NOT booking_id — so a caller-supplied real booking id survives untouched.
      const merged: Record<string, unknown> = {
        ...seed,
        ...extra,
        flow_id: flowId,
      };
      emitTrack(event, merged);
    } catch (error) {
      devWarn(`flow '${flow}' emit failed`, error);
    }
  }

  return {
    flowId,
    step(event, props) {
      stepIndex += 1;
      lastStep = event;
      emit(event, { ...props, step_index: stepIndex });
    },
    complete(event, props) {
      terminated = true;
      emit(event, { ...props });
    },
    abandon() {
      if (terminated) {
        return;
      }
      terminated = true;
      emit(FLOW_ABANDON_EVENT[flow], {
        last_step: lastStep,
        step_index: stepIndex,
        time_in_flow_ms: Date.now() - startedAt,
      });
    },
  };
}
